import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Minus, ChevronDown, Send, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getVehicles, getChecklistItems, submitCheck } from '../../lib/api';
import { getUserName } from '../../lib/auth';
import { toast } from 'sonner';
import DriverLayout from '../../components/DriverLayout';

export default function ChecklistPage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [checklist, setChecklist] = useState({});
  const [defects, setDefects] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const userName = getUserName();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [vehiclesRes, itemsRes] = await Promise.all([
        getVehicles(),
        getChecklistItems()
      ]);
      setVehicles(vehiclesRes.data);
      setChecklistItems(itemsRes.data.items);
      
      // Initialize checklist with empty values
      const initial = {};
      itemsRes.data.items.forEach(item => {
        initial[item] = '';
      });
      setChecklist(initial);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const setItemStatus = (item, status) => {
    setChecklist(prev => ({
      ...prev,
      [item]: prev[item] === status ? '' : status
    }));
  };

  const handleSubmit = async () => {
    if (!selectedVehicle) {
      toast.error('Please select a vehicle');
      return;
    }

    // Check if all items are checked
    const unchecked = checklistItems.filter(item => !checklist[item]);
    if (unchecked.length > 0) {
      toast.error(`Please check all items. ${unchecked.length} items remaining.`);
      return;
    }

    setSubmitting(true);
    try {
      const checklistData = checklistItems.map(item => ({
        item,
        status: checklist[item]
      }));

      await submitCheck({
        vehicle_id: selectedVehicle,
        checklist: checklistData,
        defects: defects.trim() || null,
        comments: comments.trim() || null
      });

      toast.success('Vehicle check submitted successfully!');
      navigate('/driver/history');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit check');
    } finally {
      setSubmitting(false);
    }
  };

  const completedCount = Object.values(checklist).filter(v => v).length;
  const totalCount = checklistItems.length;
  const hasFailures = Object.values(checklist).some(v => v === 'fail');

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="pb-24">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-blue-900">Walk-Around Check</h1>
          <p className="text-slate-500">Hello, {userName}</p>
        </div>

        {/* Vehicle Selection */}
        <Card className="mb-6 border-slate-200" data-testid="vehicle-selection-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Select Vehicle</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger className="h-12" data-testid="vehicle-select">
                <SelectValue placeholder="Choose a vehicle..." />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map(v => (
                  <SelectItem key={v.id} value={v.id} data-testid={`vehicle-option-${v.id}`}>
                    <span className="font-mono font-semibold">{v.registration}</span>
                    <span className="text-slate-500 ml-2">({v.make} {v.model})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vehicles.length === 0 && (
              <p className="text-amber-600 text-sm mt-2">No vehicles available. Contact admin.</p>
            )}
          </CardContent>
        </Card>

        {/* Progress */}
        <div className="mb-6 bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-slate-700">Progress</span>
            <span className="font-bold text-blue-900">{completedCount}/{totalCount}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-yellow-500 transition-all duration-300"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>

        {/* Checklist Items */}
        <div className="space-y-3 stagger-children" data-testid="checklist-items">
          {checklistItems.map((item, index) => (
            <div 
              key={item}
              className="checklist-item bg-white rounded-xl p-4 border border-slate-200"
              data-testid={`checklist-item-${index}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <span className="font-medium text-slate-900 text-lg">{item}</span>
                <div className="check-toggle">
                  <button
                    type="button"
                    className={`check-btn pass ${checklist[item] === 'pass' ? 'selected' : ''}`}
                    onClick={() => setItemStatus(item, 'pass')}
                    data-testid={`pass-btn-${index}`}
                  >
                    <Check className="w-4 h-4 inline mr-1" />
                    Pass
                  </button>
                  <button
                    type="button"
                    className={`check-btn fail ${checklist[item] === 'fail' ? 'selected' : ''}`}
                    onClick={() => setItemStatus(item, 'fail')}
                    data-testid={`fail-btn-${index}`}
                  >
                    <X className="w-4 h-4 inline mr-1" />
                    Fail
                  </button>
                  <button
                    type="button"
                    className={`check-btn na ${checklist[item] === 'na' ? 'selected' : ''}`}
                    onClick={() => setItemStatus(item, 'na')}
                    data-testid={`na-btn-${index}`}
                  >
                    <Minus className="w-4 h-4 inline mr-1" />
                    N/A
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Defects */}
        <Card className="mt-6 border-slate-200" data-testid="defects-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              State Any Defects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Describe any defects found..."
              value={defects}
              onChange={(e) => setDefects(e.target.value)}
              className="min-h-24"
              data-testid="defects-input"
            />
          </CardContent>
        </Card>

        {/* Comments */}
        <Card className="mt-4 border-slate-200" data-testid="comments-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Additional Comments</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Any additional notes..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="min-h-20"
              data-testid="comments-input"
            />
          </CardContent>
        </Card>

        {/* Warning if failures */}
        {hasFailures && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">Issues Detected</p>
              <p className="text-red-600 text-sm">One or more items have failed. Please ensure defects are documented.</p>
            </div>
          </div>
        )}

        {/* Submit Button - Fixed at bottom */}
        <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-slate-200 sm:relative sm:bottom-auto sm:mt-6 sm:p-0 sm:bg-transparent sm:border-0">
          <Button
            onClick={handleSubmit}
            disabled={submitting || completedCount < totalCount}
            className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-blue-950 font-bold text-lg btn-press"
            data-testid="submit-check-btn"
          >
            {submitting ? (
              <div className="spinner w-5 h-5 border-2 border-blue-950 border-t-transparent" />
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Check
              </>
            )}
          </Button>
        </div>
      </div>
    </DriverLayout>
  );
}
